import { Link as LinkIcon } from "@mui/icons-material";
import React from "react";

interface TableProps {
  columns: string[];
  data: Array<Record<string, any>>;
  setSelectedOrder: React.Dispatch<
    React.SetStateAction<{ id: number; status: string } | null>
  >;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  statusFilter?: string;
  fetchLinkedOrders?: (orderId: number) => void;
}

const TransferOrderTableComponent: React.FC<TableProps> = ({
  columns,
  data,
  setSelectedOrder,
  currentPage,
  setCurrentPage,
  totalPages,
  statusFilter,
  fetchLinkedOrders,
}) => {
  const getStatusBadge = (
    status: string
  ): { className: string; label: string } => {
    const statusMap: Record<string, { className: string; label: string }> = {
      DRAFT: {
        className: "bg-gray-100 text-gray-800 border border-gray-300",
        label: "Draft",
      },
      CREATED: {
        className: "bg-blue-100 text-blue-800 border border-blue-300",
        label: "Created",
      },
      COMPLETED: {
        className: "bg-green-100 text-green-800 border border-green-300",
        label: "Completed",
      },
      APPROVED: {
        className: "bg-yellow-100 text-yellow-800 border border-yellow-300",
        label: "Approved",
      },
      PICKED: {
        className: "bg-red-100 text-red-800 border border-red-300",
        label: "Picked",
      },
      IN_TRANSIT: {
        className: "bg-purple-100 text-purple-800 border border-purple-300",
        label: "In Transit",
      },
      CANCELED: {
        className: "bg-gray-100 text-gray-800 border border-gray-300",
        label: "Cancelled",
      },
    };
    return (
      statusMap[status] || {
        className: "bg-gray-100 text-gray-800 border border-gray-300",
        label: status,
      }
    );
  };

  const handleRowClick = (rowId: number, status: string) => {
    setSelectedOrder({ id: rowId, status });
  };

  const getActionButton = (row: any) => {
    if (row.Status === "COMPLETED") {
      return (
        <button
          onClick={() => handleRowClick(row.ID, row.Status)}
          className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors">
          View
        </button>
      );
    } else {
      return (
        <button
          onClick={() => handleRowClick(row.ID, row.Status)}
          className="px-3 py-1.5 bg-green-600 text-white rounded-md text-xs font-medium hover:bg-green-700 transition-colors">
          Open
        </button>
      );
    }
  };

  const getRowNumber = (index: number) => {
    return currentPage * 10 + index + 1;
  };

  const renderLinkedOrdersCell = (orderId: number) => {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (fetchLinkedOrders) {
            fetchLinkedOrders(orderId);
          }
        }}
        className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
        title="View linked orders">
        <LinkIcon fontSize="small" />
      </button>
    );
  };

  const getCategoryTextClass = (category: string) => {
    const normalizedCategory = category.trim().toUpperCase();

    if (normalizedCategory === "WORKING") {
      return "text-green-600 font-medium";
    }

    if (
      normalizedCategory === "NOT WORKING" ||
      normalizedCategory === "NOT_WORKING"
    ) {
      return "text-red-600 font-medium";
    }

    return "text-gray-900";
  };

  return (
    <>
      <div className="w-full overflow-x-auto">
        <table className="min-w-[900px] bg-white">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="w-16 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                #
              </th>
              {columns.map((column) => (
                <th
                  key={column}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {column}
                </th>
              ))}
              <th className="w-24 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.length > 0 ? (
              data.map((row, index) => {
                const rowNumber = getRowNumber(index);
                const isHighlighted = row.Status === "COMPLETED";
                const statusInfo = getStatusBadge(row.Status);

                return (
                  <tr
                    key={row.ID}
                    className={`hover:bg-gray-50 transition-colors ${
                      isHighlighted
                        ? "bg-blue-50 border-l-4 border-l-blue-500"
                        : ""
                    }`}
                    onClick={() => handleRowClick(row.ID, row.Status)}>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {rowNumber}
                    </td>
                    {columns.map((column) => (
                      <td
                        key={column}
                        className="px-4 py-4 text-sm text-gray-900">
                        {column === "Status" ? (
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.className}`}>
                            {statusInfo.label}
                          </span>
                        ) : column === "Category" &&
                          row[column] !== undefined &&
                          row[column] !== null ? (
                          <span className={getCategoryTextClass(String(row[column]))}>
                            {row[column]}
                          </span>
                        ) : column === "Linked TOs" ? (
                          renderLinkedOrdersCell(row[column])
                        ) : row[column] !== undefined &&
                          row[column] !== null ? (
                          row[column]
                        ) : (
                          "N/A"
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-4">{getActionButton(row)}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={columns.length + 2}
                  className="px-4 py-12 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <svg
                      className="w-12 h-12 text-gray-300 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                      />
                    </svg>
                    <p className="text-lg font-medium text-gray-900 mb-1">
                      No transfer orders found
                    </p>
                    <p className="text-gray-500">
                      Try adjusting your search or filter criteria
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}>
              First
            </button>

            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
              disabled={currentPage === 0}
              className={`p-2 rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
              }
              disabled={currentPage === totalPages - 1}
              className={`p-2 rounded-md border ${
                currentPage === totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            <button
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage === totalPages - 1}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage === totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}>
              Last
            </button>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-700">
              Page {currentPage + 1} of {totalPages}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default TransferOrderTableComponent;
